import SebService from '@/shared/application/seb-service-proxy'
import SebStringTools from '@/shared/application/seb-string-tools'
import Seb from '@/shared/application/seb'
import AuthStore from '@/store/auth-store'
import { Component } from 'vue-property-decorator'
import { CancelTokenSource } from 'axios'
import SweetAlertService from '@/services/SweetAlertService'
import IPagedList from '../models/shared/paged-list'

@Component
export default class SebComponentBase extends SweetAlertService {
  protected sebService: SebService = new SebService()
  protected seb = Seb
  protected authStore = AuthStore
  protected stringUtils = SebStringTools
  protected requiredError = (v: any) => !!v || this.t('RequiredField')
  protected emailError = (v: any) => /.+@.+/.test(v) || this.t('Errors.EmailValidationError')
  protected cancelTokenSource: CancelTokenSource | undefined
  protected formFields: any[] = []
  protected errors: any = {}

  public beforeDestroy () {
    this.cancelToken()
  }

  protected passwordMatchError (password: string, passwordRepeat: string) {
    return password === passwordRepeat ? '' : this.$t('Errors.PasswordsMustMatch').toString()
  }

  protected hideModal (id: string) {
    this.$root.$emit('bv::hide::modal', id)
  }

  protected showModal (id: string) {
    this.$root.$emit('bv::show::modal', id)
  }

  protected t (key: string): string {
    return this.$t(key).toString()
  }

  /**
   * Load items used into selectBox
   * @param requestUrl
   * @param callback
   */
  protected loadSelectItemList<T> (requestUrl: string, callback: any, tries = 0) {
    this.sebService.get<IPagedList<T>>(requestUrl, false).then((response) => {
      if (!response.isError) {
        const content = response.content
        callback(content.items)
      } else {
        if (response.errors instanceof Array) {
          let functionalMessage = ''
          Array.prototype.forEach.call(response.errors, (error) => {
            if (error.type === 1) {
              functionalMessage += `${this.$t(error.name).toString()} (E${error.value}). `
            } else {
              this.toasterAlert(`${this.$t(error.name).toString()} (E${error.value})`)
            }
          })
          if (functionalMessage) {
            this.swalAlert('error', functionalMessage)
          }
        } else if (tries < 3) {
          // Error
          setTimeout(() => {
            // callback function for timer, gets called after the time-delay
            this.loadSelectItemList(requestUrl, callback, tries++)
          }, 1000) // This promise will be resolved in 2000 milli-seconds
        } else {
          this.toasterAlert(this.$t('Swal.FrontEndError').toString())
        }
      }
    })
  }

  protected deleteItem (requestUrl: string, _callback?: (val: boolean) => any) {
    this.swalConfirm(this.$t('Swal.AreYouSureToDelete').toString()).then((result) => {
      if (result.value) {
        this.sebService.delete(requestUrl).then((response) => {
          if (!response.isError) {
            _callback && _callback(true)
            this.swalToast(2000, 'success', this.$t('Swal.Successful').toString())
          } else {
            if (response.errors.length !== 0) {
              _callback && _callback(false)
              this.swalAlert('error', this.$t(response.errors[0].name).toString())
            }
          }
        })
      }
    })
  }

  protected getItem<T> (requestUrl: string, callback: any) {
    this.sebService.get<T>(requestUrl).then((response) => {
      if (!response.isError) {
        const content = response.content
        callback(content)
      } else {
        if (response.errors.length !== 0) {
          let functionalMessage = ''
          response.errors.forEach((error) => {
            if (error.type === 1) {
              functionalMessage += `${this.$t(error.name).toString()} (E${error.value}). `
            } else {
              this.toasterAlert(`${this.$t(error.name).toString()} (E${error.value})`)
            }
          })
          if (functionalMessage) {
            this.swalAlert('error', functionalMessage)
          }
        } else {
          this.toasterAlert(this.$t('Swal.FrontEndError').toString())
        }
      }
    })
  }

  protected createOrUpdateItem (requestUrl: string, obj: any, _callback: (...args: any[]) => any) {
    this.sebService.post<void>(requestUrl, obj).then((response) => {
      if (!response.isError) {
        this.swalToast(2000, 'success', this.$t('Swal.Successful').toString())
        _callback(true)
      } else {
        if (response.errors.length !== 0) {
          let functionalMessage = ''
          response.errors.forEach((error) => {
            if (error.type === 1) {
              functionalMessage += `${this.$t(error.name).toString()} (E${error.value}). `
            } else {
              this.toasterAlert(`${this.$t(error.name).toString()} (E${error.value})`)
            }
          })
          if (functionalMessage) {
            this.swalAlert('error', functionalMessage)
          }
        } else {
          this.toasterAlert(this.$t('Swal.FrontEndError').toString())
        }
      }
    })
  }

  protected emptyObject (obj) {
    Object.keys(obj).forEach((k) => delete obj[k])
  }

  protected validateState (ref: string) {
    if (this.formFields[ref] && (this.formFields[ref].dirty || this.formFields[ref].validated)) {
      return !this.errors.has(ref)
    }
    return null
  }

  protected convertModelToQueryString (params: any): string {
    return Object.keys(params).reduce((previous, current, idx) => {
      const currentParam = '$' + current + '=' + params[current]
      if (idx === 1) {
        return '$' + previous + '=' + params[previous] + '&' + currentParam
      }
      return previous + '&' + currentParam
    })
  }

  protected cancelToken (): void {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('cancel-request was cancelled by another request')
    }
  }

  protected clipboard (text: string) {
    SebStringTools.copyToClipboard(text)
    this.swalToast(2000, 'success', this.$t('Copied').toString())
  }
}
